import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CreditCard, CheckCircle2, Send, Settings2, QrCode, Plus, MessageSquare } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useVendorPaymentConfigs, useVendorPaymentRequests, useVendorRefresh } from "@/hooks/useVendor";
import { formatDate } from "@/lib/format";
import { VImageUpload } from "@/components/vendor/VImageUpload";
import { AdminModal, Field, inputCls, textareaCls } from "@/pages/admin/ui";

const TYPE_LABEL = { link: "Paiement par lien", manual: "Paiement manuel (numéro)", qr: "Paiement par QR Code", other: "Autre" };
const EMPTY_CFG = { payment_type: "manual", payment_number: "", beneficiary_name: "", link: "",
  qr_image_url: "", logo_url: "", address: "", network: "", instructions: "", is_active: true };
const EMPTY_REQ = { method_name: "", description: "", payment_number: "", beneficiary_name: "", link: "",
  address: "", network: "", payment_type: "manual", extra_info: "", instructions: "",
  logo_url: "", image_url: "", qr_image_url: "", message: "" };

const REQ_STATUS = {
  approved: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "✅ Approuvée" },
  rejected: { cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Refusée" },
  info_requested: { cls: "bg-sky-50 text-sky-700 border-sky-200", label: "ℹ️ Infos demandées" },
  pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "🟠 En attente d'approbation" },
};

export default function VendorPayments() {
  const { data: cfgData, isLoading } = useVendorPaymentConfigs();
  const { data: requests } = useVendorPaymentRequests();
  const refresh = useVendorRefresh();
  const [open, setOpen] = useState(false);
  const [req, setReq] = useState(EMPTY_REQ);
  const [saving, setSaving] = useState(false);
  const [editMethod, setEditMethod] = useState(null);
  const [cfg, setCfg] = useState(EMPTY_CFG);
  const [uploading, setUploading] = useState("");

  const methods = cfgData?.methods || [];
  const setR = (k, v) => setReq((r) => ({ ...r, [k]: v }));

  const openRequest = () => { setReq(EMPTY_REQ); setOpen(true); };

  const submit = async () => {
    if (!req.method_name.trim()) { toast.error("Indiquez le nom du moyen de paiement"); return; }
    setSaving(true);
    try {
      await api.post("/vendor/payment-requests", req);
      toast.success("Demande envoyée à l'administrateur.");
      setOpen(false); setReq(EMPTY_REQ); refresh(["vendor-payment-requests"]);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const openConfig = (m) => {
    setEditMethod(m.method_name);
    setCfg({ ...EMPTY_CFG, payment_type: m.payment_type, payment_number: m.payment_number,
      beneficiary_name: m.beneficiary_name, link: m.link, qr_image_url: m.qr_image_url,
      logo_url: m.logo_url, address: m.address, network: m.network, instructions: m.instructions,
      is_active: m.is_active });
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/vendor/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCfg((c) => ({ ...c, [kind]: data.url }));
      toast.success("Image téléversée");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(""); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/vendor/payment-configs/${encodeURIComponent(editMethod)}`, { ...cfg, method_name: editMethod });
      toast.success("Informations enregistrées — visibles par vos clients sur la page de paiement");
      setEditMethod(null); refresh(["vendor-payment-configs"]);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="vendor-payments">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Moyens de paiement</h1>
          <p className="mt-1 text-sm text-slate-500">Moyens activés par l'administrateur — renseignez VOS informations de réception.</p>
        </div>
        <button onClick={openRequest} data-testid="request-payment-method"
          className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark">
          <Send className="h-4 w-4" /> Demander un moyen de paiement
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : methods.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center" data-testid="no-payment-methods">
          <CreditCard className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucun moyen de paiement n'est actuellement activé pour votre boutique.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {methods.map((m) => (
            <div key={m.method_name} className="rounded-2xl border border-line bg-white p-4" data-testid={`vmethod-${m.method_name}`}>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                  {m.logo_url ? <img src={fileUrl(m.logo_url)} alt="" className="h-full w-full object-contain" /> : <CreditCard className="h-5 w-5 text-slate-400" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-ink">{m.method_name}</p>
                  <span className={`flex items-center gap-1 text-xs font-bold ${m.configured ? "text-emerald-600" : "text-amber-600"}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> {m.configured ? "Configuré" : "À configurer"}
                  </span>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">{TYPE_LABEL[m.payment_type] || m.payment_type}</p>
              {m.payment_number && <p className="mt-1 font-mono text-xs font-bold text-ink">{m.payment_number}</p>}
              <button onClick={() => openConfig(m)} data-testid={`vmethod-config-${m.method_name}`}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-line py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand">
                <Settings2 className="h-3.5 w-3.5" /> Configurer mes informations
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Carte : Demander un nouveau moyen de paiement */}
      <button onClick={openRequest} data-testid="request-new-method-card"
        className="flex w-full items-center gap-4 rounded-2xl border-2 border-dashed border-brand/40 bg-brand-soft p-5 text-left transition-colors duration-200 hover:border-brand">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand text-white"><Plus className="h-6 w-6" /></span>
        <div className="flex-1">
          <p className="font-display text-base font-bold text-ink">Demander un nouveau moyen de paiement</p>
          <p className="text-sm text-slate-500">Un moyen de paiement n'est pas disponible ? Demandez à l'administrateur de l'ajouter à votre compte.</p>
        </div>
        <Send className="h-5 w-5 text-brand" />
      </button>

      {requests?.length > 0 && (
        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="font-display text-base font-bold text-ink">Mes demandes</p>
          <div className="mt-3 space-y-3">
            {requests.map((r) => {
              const st = REQ_STATUS[r.status] || REQ_STATUS.pending;
              return (
                <div key={r.id} data-testid={`vpay-request-${r.id}`} className="rounded-xl border border-line p-3.5 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="font-bold text-ink">{r.methods?.join(", ") || r.method_name}</span>
                      {r.description && <p className="text-xs text-slate-500">{r.description}</p>}
                      <span className="text-xs text-slate-400">{formatDate(r.created_at)}</span>
                    </div>
                    <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-bold ${st.cls}`}>{st.label}</span>
                  </div>
                  {r.admin_reply && (
                    <div className="mt-2 flex items-start gap-2 rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600" data-testid={`vpay-reply-${r.id}`}>
                      <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
                      <span><b>Réponse admin :</b> {r.admin_reply}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-brand/20 bg-brand-soft p-4 text-xs text-slate-700">
        ℹ️ L'administrateur active les moyens de paiement pour votre boutique. Une fois activé, vous renseignez
        vos propres informations (numéro, lien, QR Code, adresse, réseau, instructions). Vos clients verront
        uniquement VOS informations lors du paiement de vos produits.
      </div>

      {/* Formulaire de demande de moyen de paiement */}
      <AdminModal open={open} onOpenChange={setOpen} title="Demander un moyen de paiement" wide>
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Renseignez les informations du moyen de paiement à demander à l'administrateur.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nom du moyen de paiement *"><input value={req.method_name} onChange={(e) => setR("method_name", e.target.value)} className={inputCls} data-testid="req-name" placeholder="Ex : PayPal, Orange Money…" /></Field>
            <Field label="Type de paiement">
              <select value={req.payment_type} onChange={(e) => setR("payment_type", e.target.value)} className={inputCls} data-testid="req-type">
                {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description"><textarea rows={2} value={req.description} onChange={(e) => setR("description", e.target.value)} className={textareaCls} data-testid="req-description" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Numéro"><input value={req.payment_number} onChange={(e) => setR("payment_number", e.target.value)} className={inputCls} data-testid="req-number" /></Field>
            <Field label="Nom du titulaire"><input value={req.beneficiary_name} onChange={(e) => setR("beneficiary_name", e.target.value)} className={inputCls} data-testid="req-holder" /></Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Lien de paiement"><input value={req.link} onChange={(e) => setR("link", e.target.value)} className={inputCls} placeholder="https://…" data-testid="req-link" /></Field>
            <Field label="Adresse de paiement"><input value={req.address} onChange={(e) => setR("address", e.target.value)} className={inputCls} data-testid="req-address" /></Field>
          </div>
          <Field label="Réseau"><input value={req.network} onChange={(e) => setR("network", e.target.value)} className={inputCls} placeholder="TRC20, ERC20…" data-testid="req-network" /></Field>
          <Field label="Informations supplémentaires"><textarea rows={2} value={req.extra_info} onChange={(e) => setR("extra_info", e.target.value)} className={textareaCls} data-testid="req-extra" /></Field>
          <Field label="Instructions pour les clients"><textarea rows={2} value={req.instructions} onChange={(e) => setR("instructions", e.target.value)} className={textareaCls} data-testid="req-instructions" /></Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <VImageUpload label="Logo" value={req.logo_url} onChange={(v) => setR("logo_url", v)} testId="req-logo" />
            <VImageUpload label="Image" value={req.image_url} onChange={(v) => setR("image_url", v)} testId="req-image" />
            <VImageUpload label="QR Code" value={req.qr_image_url} onChange={(v) => setR("qr_image_url", v)} testId="req-qr" />
          </div>
          <Field label="Message pour l'administrateur"><textarea rows={2} value={req.message} onChange={(e) => setR("message", e.target.value)} className={textareaCls} data-testid="req-message" /></Field>
          <button onClick={submit} data-testid="req-submit" disabled={saving} className="w-full rounded-full bg-brand py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Envoyer la demande"}
          </button>
        </div>
      </AdminModal>

      {/* Configuration de SON moyen de paiement */}
      <AdminModal open={!!editMethod} onOpenChange={() => setEditMethod(null)} title={`Configurer — ${editMethod || ""}`} wide>
        <div className="space-y-4">
          <Field label="Type de paiement">
            <select value={cfg.payment_type} onChange={(e) => setCfg((c) => ({ ...c, payment_type: e.target.value }))} className={inputCls} data-testid="cfg-type">
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Numéro de paiement">
              <input value={cfg.payment_number} onChange={(e) => setCfg((c) => ({ ...c, payment_number: e.target.value }))} className={inputCls} placeholder="+225 ..." data-testid="cfg-number" />
            </Field>
            <Field label="Nom du bénéficiaire">
              <input value={cfg.beneficiary_name} onChange={(e) => setCfg((c) => ({ ...c, beneficiary_name: e.target.value }))} className={inputCls} />
            </Field>
          </div>
          <Field label="Lien de paiement">
            <input value={cfg.link} onChange={(e) => setCfg((c) => ({ ...c, link: e.target.value }))} className={inputCls} placeholder="https://..." data-testid="cfg-link" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Adresse (crypto / wallet)">
              <input value={cfg.address} onChange={(e) => setCfg((c) => ({ ...c, address: e.target.value }))} className={inputCls} data-testid="cfg-address" />
            </Field>
            <Field label="Réseau">
              <input value={cfg.network} onChange={(e) => setCfg((c) => ({ ...c, network: e.target.value }))} className={inputCls} placeholder="TRC20, ERC20..." data-testid="cfg-network" />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="QR Code">
              <div className="flex items-center gap-2">
                {cfg.qr_image_url && <img src={fileUrl(cfg.qr_image_url)} alt="" className="h-12 w-12 rounded-lg border border-line object-contain" />}
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-line px-3 py-2.5 text-xs font-bold text-slate-500 hover:border-brand hover:text-brand">
                  {uploading === "qr_image_url" ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                  {cfg.qr_image_url ? "Changer" : "Téléverser le QR"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload("qr_image_url", e.target.files?.[0])} data-testid="cfg-qr" />
                </label>
              </div>
            </Field>
            <Field label="Logo du moyen">
              <div className="flex items-center gap-2">
                {cfg.logo_url && <img src={fileUrl(cfg.logo_url)} alt="" className="h-12 w-12 rounded-lg border border-line object-contain" />}
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-line px-3 py-2.5 text-xs font-bold text-slate-500 hover:border-brand hover:text-brand">
                  {uploading === "logo_url" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  {cfg.logo_url ? "Changer" : "Téléverser le logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload("logo_url", e.target.files?.[0])} data-testid="cfg-logo" />
                </label>
              </div>
            </Field>
          </div>
          <Field label="Instructions pour vos clients" hint="Une instruction par ligne, affichée sur la page de paiement.">
            <textarea rows={3} value={cfg.instructions} onChange={(e) => setCfg((c) => ({ ...c, instructions: e.target.value }))} className={textareaCls} data-testid="cfg-instructions"
              placeholder={"1. Envoyez le montant exact au numéro ci-dessus\n2. Renseignez la référence de transaction"} />
          </Field>
          <label className="flex items-center gap-2 text-sm font-bold text-ink">
            <input type="checkbox" checked={cfg.is_active} onChange={(e) => setCfg((c) => ({ ...c, is_active: e.target.checked }))} className="h-4 w-4 accent-brand" data-testid="cfg-active" />
            Afficher ce moyen de paiement à mes clients
          </label>
          <button onClick={saveConfig} disabled={saving} data-testid="cfg-save"
            className="w-full rounded-full bg-brand py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Enregistrer mes informations"}
          </button>
        </div>
      </AdminModal>
    </div>
  );
}
